import React from 'react'
import { Card, List, Typography, Spin, Button, Tag } from 'antd'
import { useNavigate, useLocation, Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { apiUrl } from '../config/api'
import { formatAmount } from '../utils/amount'
import { CalendarOutlined, RightOutlined } from '@ant-design/icons'

const { Title, Text, Paragraph } = Typography

export default function MyOrders() {
  const navigate = useNavigate()
  const { state } = useLocation()
  const fromOrderSuccess = state?.fromOrderSuccess === true

  const accessToken = React.useMemo(() => {
    try { return localStorage.getItem('accessToken') || '' } catch { return '' }
  }, [])

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['myOrders', accessToken],
    enabled: !!accessToken,
    queryFn: async () => {
      const res = await fetch(apiUrl('/api/orders/my'), {
        headers: {
          'Accept': 'application/json',
          'Authorization': `Bearer ${accessToken}`
        }
      })
      if (!res.ok) {
        const msg = res.status === 401 ? 'الرجاء تسجيل الدخول لعرض طلباتك' : `فشل في جلب الطلبات (${res.status})`
        throw new Error(msg)
      }
      return res.json()
    }
  })

  const orders = Array.isArray(data) ? data : []

  const formatDate = (dateStr) => {
    if (!dateStr) return '-'
    try {
      const d = new Date(dateStr)
      return new Intl.DateTimeFormat('ar-EG', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      }).format(d)
    } catch {
      return dateStr
    }
  }

  if (!accessToken) {
    return (
      <div style={{ display: 'grid', placeItems: 'center', minHeight: '60vh', padding: 24 }}>
        <Card style={{ textAlign: 'center', maxWidth: 400, borderRadius: 12 }}>
          <Title level={4}>يرجى تسجيل الدخول</Title>
          <Paragraph>الرجاء تسجيل الدخول لعرض قائمة طلباتك السابقة.</Paragraph>
          <Button
            type="primary"
            size="large"
            onClick={() => window.dispatchEvent(new CustomEvent('om:openLogin'))}
            style={{ marginTop: 16, background: '#f55905', borderColor: '#f55905' }}
          >
            تسجيل الدخول
          </Button>
        </Card>
      </div>
    )
  }

  return (
    <div style={{ maxWidth: 800, margin: '0 auto', direction: 'rtl' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <Title level={3} style={{ margin: 0 }}>طلباتي</Title>
        <Button icon={<RightOutlined />} onClick={() => navigate('/categories')}>رجوع</Button>
      </div>

      {fromOrderSuccess && (
        <Card style={{ marginBottom: 24, background: '#f6ffed', borderColor: '#b7eb8f', borderRadius: 12 }}>
          <Paragraph style={{ margin: 0, fontSize: 16 }}>
            تم تأكيد طلبك وهو قيد المعالجة.{' '}<br/>
            <Link to="/categories">اضغط هنا لمتابعة التسوّق</Link>
          </Paragraph>
        </Card>
      )}

      {isLoading && (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '100px 0' }}>
          <Spin size="large" tip="جاري تحميل طلباتك..." />
        </div>
      )}

      {isError && (
        <Card style={{ textAlign: 'center', borderRadius: 12 }}>
          <Text type="danger" style={{ display: 'block', marginBottom: 16, fontSize: 16 }}>
            {error?.message || 'حدث خطأ أثناء جلب الطلبات'}
          </Text>
          <Button onClick={() => refetch()} type="primary">إعادة المحاولة</Button>
        </Card>
      )}

      {!isLoading && !isError && orders.length === 0 && (
        <Card style={{ textAlign: 'center', padding: '40px 0', borderRadius: 12 }}>
          <Typography.Paragraph style={{ fontSize: 16, color: '#888' }}>
            لا توجد لديك طلبات سابقة حتى الآن.
          </Typography.Paragraph>
          <Button type="primary" onClick={() => navigate('/categories')} style={{ background: '#f55905', borderColor: '#f55905' }}>
            ابدأ التسوق الآن
          </Button>
        </Card>
      )}

      {!isLoading && !isError && orders.length > 0 && (
        <List
          dataSource={orders}
          renderItem={(order) => (
            <Card
              key={order.id}
              hoverable
              onClick={() => navigate(`/orders/${order.id}`)}
              style={{
                marginBottom: 12,
                borderRadius: 12,
                boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                transition: 'all 0.2s'
              }}
              bodyStyle={{ padding: '16px 20px' }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ display: 'grid', gap: 4 }}>
                  <Text strong style={{ fontSize: 17 }}>طلب رقم #{order.id}</Text>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#888' }}>
                    <CalendarOutlined style={{ fontSize: 12 }} />
                    <Text type="secondary" style={{ fontSize: 13 }}>{formatDate(order.createdAt)}</Text>
                  </div>
                </div>

                <div style={{ textAlign: 'left', display: 'flex', flexDirection: 'column', gap: 4 }}>
                  <Text type="secondary" style={{ fontSize: 12 }}>الإجمالي الكلي</Text>
                  <Text strong style={{ fontSize: 20, color: '#f55905' }}>
                    {formatAmount(order.totalAfterDiscount)}
                  </Text>
                </div>
              </div>
            </Card>
          )}
        />
      )}
    </div>
  )
}
